import React from 'react';
import { Spin, Table, Pagination } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import type { UsageHistoryProps } from './types';
import { getUsageList } from '@/api/subtitles';
import { localizeTime } from '@/utils/i18n';
import Empty from "@/components/record/empty";
import dayjs from 'dayjs';
import styles from '@/components/style/index.module.less';
import './styles/index.less';

const UsageHistory: React.FC<UsageHistoryProps> = ({
  usageCurr,
  usageTotal,
  usageSize,
  onUsagePaginationChange
}) => {
  const { t } = useTranslation();

  const columns: ColumnsType<API.DeductionItem> = [
    {
      title: t('startTime'),
      dataIndex: 'start_time',
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: t('endTime'),
      dataIndex: 'end_time',
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: t('useTime'),
      dataIndex: 'deduction_amount',
      render: (text) => localizeTime(text),
    },
  ];

  const getUsageRequest = useQuery({
    queryKey: ['getUsage', usageCurr, usageSize],
    queryFn: () => getUsageList(usageCurr, usageSize),
  });

  return (
    <div className="enhanced-usage-history">
      <div className="usage-history-card">
        <div className="card-header">
          <div className="header-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
          </div>
          <div className="header-content">
            <h2 className="card-title">
              {t('account.usageHistory') || 'Usage History'}
            </h2>
            <p className="card-subtitle">
              {t('account.viewUsageHistoryAndTrends') || 'View your usage history and track usage trends'}
            </p>
          </div>
        </div>
        
        <div className="card-content">
          {getUsageRequest.isLoading && (
            <div className="loading-container">
              <Spin size="large" />
            </div>
          )}
          
          {getUsageRequest.data?.data.data.records.length === 0 && (
            <div className="empty-container">
              <Empty text={t('noUse')} />
            </div>
          )}
          
          {getUsageRequest.data?.data.data.records?.length > 0 && (
            <>
              <div className="table-container">
                <Table
                  rowKey="deduction_id"
                  pagination={false}
                  columns={columns}
                  dataSource={getUsageRequest.data?.data.data.records}
                  loading={getUsageRequest.isLoading}
                  className="custom-table"
                  locale={{ emptyText: t('NoData') }}
                  bordered={false}
                />
              </div>
              
              <div className="pagination-container">
                <Pagination 
                  className={styles.paginationRecord} 
                  pageSize={usageSize}
                  showSizeChanger={false} 
                  current={usageCurr} 
                  total={usageTotal} 
                  hideOnSinglePage 
                  onChange={onUsagePaginationChange} 
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsageHistory;